import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CreateSquadModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { name: string; description: string }) => void;
  isLoading?: boolean;
}

export const CreateSquadModal: React.FC<CreateSquadModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [squadName, setSquadName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');

  const handleConfirm = () => {
    if (!squadName.trim()) {
      setNameError(t('adminMainPage.squad.createModal.squadNameRequired'));
      return;
    }

    onConfirm({
      name: squadName.trim(),
      description: description.trim(),
    });

    // Reset form
    setSquadName('');
    setDescription('');
    setNameError('');
  };

  const handleClose = () => {
    setSquadName('');
    setDescription('');
    setNameError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '24px', fontWeight: 600 }}>
        {t('adminMainPage.squad.createModal.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('adminMainPage.squad.createModal.squadName')}{' '}
              <span style={{ color: theme.palette.error.main }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder={t('adminMainPage.squad.createModal.squadNamePlaceholder')}
              value={squadName}
              onChange={(e) => {
                setSquadName(e.target.value);
                if (nameError) setNameError('');
              }}
              error={!!nameError}
              helperText={nameError}
              disabled={isLoading}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('adminMainPage.squad.createModal.description')}{' '}
              <span style={{ color: theme.palette.text.secondary }}>
                {t('adminMainPage.squad.createModal.descriptionOptional')}
              </span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={t('adminMainPage.squad.createModal.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.info.light,
              borderRadius: theme.shape.borderRadius,
              border: `1px solid ${theme.palette.info.main}`,
            }}>
            <Typography variant="body2" sx={{ fontSize: '14px', lineHeight: 1.6 }}>
              <strong>{t('adminMainPage.squad.createModal.noteTitle')}</strong>{' '}
              {t('adminMainPage.squad.createModal.noteText')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: 'none' }}>
          {t('adminMainPage.squad.createModal.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          sx={{ textTransform: 'none' }}>
          {isLoading
            ? t('adminMainPage.squad.createModal.creating')
            : t('adminMainPage.squad.createModal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
